import { defineStore } from 'pinia'
import TextCell from '@/onenetwork/components/datatable/TextCell.vue'
import DateTimeCell from '@/onenetwork/components/datatable/DateTimeCell.vue'
import TagCell from '@/onenetwork/components/datatable/TagCell.vue'
import ActionCell from '@/modules/push-campaign/components/ActionCell.vue'
import { uuid } from 'vue3-uuid'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import { PushCampaignAPI } from '../constants/push-campaign.api.uri.const'
import { CAMPAIGN_STATUS, formatTableDataPushCampaign } from '../helper/table'
import { markRaw } from 'vue'

export enum PushCampaignListActionTypes {
  FILTERING = 'FILTERING',
  PENDING = 'PENDING',
  FETCHING = 'FETCHING',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_FAILURE = 'FETCH_FAILURE',
}

interface PushCampaignListStore {
  action_type: PushCampaignListActionTypes
  action_mess: string
  uuid: string
  data: Object
  filter: Array<{
    key: string
    type: string
    label: string
    placeholder: string
    value: any
    options?: Array<{ label: string; value: string }>
  }>
  pageSize: number
  page: number
  tabs: Array<any>
  tableConfig: any
  selectedIds: Array<string | number>
}

export const usePushCampaignListStore = defineStore('push_campaign.list', {
  state: (): PushCampaignListStore => ({
    action_type: PushCampaignListActionTypes.PENDING,
    action_mess: '',
    uuid: '',
    data: {
      list: [],
      totalRecords: 0,
      totalPage: 0,
    },
    filter: [
      {
        key: 'name',
        type: 'taginput',
        label: 'Lọc theo tên chiến dịch',
        placeholder: 'Nhập tên chiến dịch',
        value: '',
      },
      {
        key: 'status',
        type: 'select-multiple',
        label: 'Lọc theo trạng thái',
        placeholder: 'Chọn trạng thái',
        value: '',
        options: CAMPAIGN_STATUS,
      },
    ],
    pageSize: 10,
    page: 1,
    tabs: [
      {
        id: 'list',
        label: 'Danh sách chiến dịch',
        active: true,
      },
    ],
    tableConfig: {
      canShowHide: true,
      columns: {
        campaign_type: { label: 'Loại chiến dịch', component: markRaw(TagCell), class: '', visible: true, sort: false, config: {} },
        status_text: { label: 'Trạng thái', component: markRaw(TagCell), class: '', visible: true, sort: false, config: {} },
        name: { label: 'Tên chiến dịch', component: markRaw(TextCell), class: '', visible: true, sort: false, config: {} },
        started_at: { label: 'Thời gian bắt đầu', component: markRaw(DateTimeCell), class: '', visible: true, sort: '', config: {} },
        ended_at: { label: 'Thời gian kết thúc', component: markRaw(DateTimeCell), class: '', visible: true, sort: '', config: {} },
        created_at: { label: 'Thời gian tạo', component: markRaw(DateTimeCell), class: '', visible: true, sort: 'desc', config: {} },
        created_by: { label: 'Người tạo', component: markRaw(TextCell), class: '', visible: true, sort: false, config: {} },
        sent_count: { label: 'Đã gửi', component: markRaw(TextCell), class: '', visible: true, sort: false, config: {} },
        actions: { label: 'Hành động', component: markRaw(ActionCell), class: '', visible: true, sort: false, config: {} },
      },
    },
    selectedIds: [],
  }),
  getters: {
    sortFilter: (state) => {
      const sort: any = []
      if (Object.keys(state.tableConfig?.columns).length > 0) {
        Object.keys(state.tableConfig?.columns).forEach((col) => {
          if (state.tableConfig.columns[col].sort) {
            sort.push([col, state.tableConfig.columns[col].sort])
          }
        })
      }
      return sort
    },
  },
  actions: {
    changeTab(targetId: string) {
      for (const tab of this.tabs) {
        tab.active = tab.id === targetId
      }
    },
    closeTab(targetId: string) {
      const index = this.tabs.findIndex((tab) => tab.id === targetId)
      if (index !== -1) {
        this.tabs.splice(index, 1)
      }
    },
    closeAllTab() {
      this.tabs = [
        {
          id: 'list',
          label: 'Danh sách chiến dịch',
          active: true,
        },
      ]
    },
    newTab(item: any) {
      const exists = this.tabs.some((tab) => tab.id === item.id)
      if (!exists) {
        this.tabs.push(item)
      }
      this.changeTab(item.id)
    },

    setFilter(filter: any) {
      this.filter = filter
      this.action_type = PushCampaignListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    resetFilter() {
      this.filter = this.filter.map((item) => {
        if (item.type === 'region-level-1' || item.type === 'region-level-2') {
          return { ...item, value: '', options: [] }
        }
        return { ...item, value: '' }
      })
      this.page = 1
      this.action_type = PushCampaignListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    changePage(page: number) {
      this.page = page
      this.action_type = PushCampaignListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },
    changePageSize(size: number) {
      this.pageSize = size
      this.action_type = PushCampaignListActionTypes.FILTERING
      this.uuid = uuid.v4()
    },

    toggleColumnVisibility(columnCode: string, visible: boolean) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].visible = visible
      }
    },
    toggleSort(columnCode: string, sort: string) {
      if (this.tableConfig?.columns[columnCode]) {
        this.tableConfig.columns[columnCode].sort = sort
      }
    },
    setSelectedRow(ids: Array<string | number>) {
      this.selectedIds = ids
    },

    async fetchList() {
      return new Promise(async (resolve, reject) => {
        try {
          this.action_type = PushCampaignListActionTypes.FETCHING
          const params = {
            page: this.page,
            page_size: this.pageSize,
          }
          const res: any = await APIClient.get(PushCampaignAPI.campaign.list, {
            params,
          })
          const { data } = res.data
          this.data = {
            list: formatTableDataPushCampaign(data.data) || [],
            totalRecords: data.total ?? 0,
            totalPage: data.last_page ?? 0,
          }
          this.action_type = PushCampaignListActionTypes.FETCH_SUCCESS
          resolve(data)
        } catch (e) {
          // Mock data fallback - TODO: remove when API is ready
          const mockData = [
            {
              id: 1,
              type: 'notification_link',
              status: 'sent',
              name: 'Tên chiến dịch',
              started_at: '2026-04-01 08:00:00',
              ended_at: '2026-04-01 08:00:00',
              created_at: '2026-04-01 08:00:00',
              created_by: { full_name: 'Thai Tran' },
              sent_count: 250,
              total_count: 1000,
            },
            {
              id: 2,
              type: 'notification_reward',
              status: 'scheduled',
              name: 'Tên chiến dịch',
              started_at: '2026-04-01 08:00:00',
              ended_at: '2026-04-01 08:00:00',
              created_at: '2026-04-01 08:00:00',
              created_by: { full_name: 'Thai Tran' },
              sent_count: 250,
              total_count: 1000,
            },
          ]
          this.data = {
            list: formatTableDataPushCampaign(mockData),
            totalRecords: 17,
            totalPage: 2,
          }
          this.action_type = PushCampaignListActionTypes.FETCH_SUCCESS
        }
      })
    },

    async stopCampaign(id: number) {
      return new Promise(async (resolve, reject) => {
        try {
          const res: any = await APIClient.post(PushCampaignAPI.campaign.stop.replace(':id', String(id)))
          await this.fetchList()
          resolve(res)
        } catch (e) {
          reject(e)
        }
      })
    },
  },
})
